import { useRef, useState } from 'react'
import { Loader2, Upload, X } from 'lucide-react'
import isError from 'lodash/isError'
import partition from 'lodash/partition'
import toArray from 'lodash/toArray'
import type { OrderEventFilePreviewContext } from '@klear/order-event/file-preview-display'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import {
  UPLOAD_ACCEPT_ATTRIBUTE,
  UPLOAD_ACCEPTED_LABEL,
  UPLOAD_MANUAL_ENTRY_NOTE,
  isUploadFile
} from '@klear/upload/upload-formats'
import type { UploadHints } from '@klear/schema/document-processing'
import { canSendSourceDocuments } from '@klear/constants/document-processing'
import { failedUploadsMessage, useSendForReview } from '@klear/ui/hooks/use-document-upload-request'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { Button } from '@klear/ui/components/button'
import { FileBadgeList } from '@klear/ui/components/file-badge-list'
import { Modal } from '@klear/ui/components/modal'
import { useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { toast } from '@klear/ui/hooks/use-toast'
import { useUploadOrderEventDocuments } from '@klear/ui/hooks/use-upload-order-event-documents'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import { trpc } from '@klear/ui/lib/trpc'

export type ReceivableFileGroup = {
  documents: readonly OrderEventDocumentRef[]
  previewContext: OrderEventFilePreviewContext
  removable?: boolean
}

export function ReceivableFiles({ groups, traderId }: { groups: readonly ReceivableFileGroup[]; traderId: number }) {
  const utils = trpc.useUtils()
  const deleteDocument = useMutationWithToast(trpc.orderEvent.deleteDocument.useMutation, {
    successTitle: null,
    errorTitle: 'Could not remove the file'
  })
  const [removing, setRemoving] = useState<OrderEventDocumentRef | null>(null)

  const hasFiles = groups.some((group) => group.documents.length > 0)
  if (!hasFiles) return <span className="text-xs text-muted-foreground">None yet</span>

  const confirmRemove = () => {
    if (removing == null) return
    deleteDocument.mutate(
      { documentId: removing.id, traderId },
      {
        onSuccess: () => void utils.documents.list.invalidate(),
        onSettled: () => setRemoving(null)
      }
    )
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {groups.flatMap((group) =>
        group.documents.map((document) => (
          <div key={document.id} className="group/file flex max-w-[220px] items-center gap-1">
            <FileBadgeList
              documents={[document]}
              previewContext={group.previewContext}
              className="min-w-0 items-start"
            />
            {group.removable && (
              <button
                type="button"
                aria-label={`Remove ${document.name}`}
                title="Remove"
                onClick={() => setRemoving(document)}
                className="grid size-4 shrink-0 place-items-center rounded text-muted-foreground opacity-0 hover:text-destructive focus-visible:opacity-100 group-hover/file:opacity-100"
              >
                <X className="size-3" />
              </button>
            )}
          </div>
        ))
      )}
      {removing && (
        <Modal
          size="sm"
          open
          onOpenChange={(open) => {
            if (!open && !deleteDocument.isPending) setRemoving(null)
          }}
          title={`Remove ${removing.name}?`}
          description="The file is removed from this document. Klear reviews what is left."
          primaryText={deleteDocument.isPending ? 'Removing…' : 'Remove file'}
          primaryDisabled={deleteDocument.isPending}
          secondaryText="Keep file"
          secondaryDisabled={deleteDocument.isPending}
          handleSecondaryAction={() => setRemoving(null)}
          handlePrimaryAction={confirmRemove}
        >
          {null}
        </Modal>
      )}
    </div>
  )
}

function FilePickerButton({
  label,
  multiple,
  onFiles
}: {
  label: string
  multiple: boolean
  onFiles: (files: File[]) => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const upload = async (chosen: File[]) => {
    const [files, refused] = partition(chosen, (file) => isUploadFile(file))
    if (refused.length > 0) {
      toast({
        title: 'Not added',
        description: `Klear takes only ${UPLOAD_ACCEPTED_LABEL}: ${refused.map((file) => file.name).join(', ')}.`,
        variant: 'destructive'
      })
    }
    if (files.length === 0) return
    setIsUploading(true)
    try {
      await onFiles(files)
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: isError(error) ? error.message : 'The files could not be added.',
        variant: 'destructive'
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        hidden
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        aria-label={`Add files to the ${label.toLowerCase()}`}
        onChange={(event) => {
          const chosen = toArray(event.target.files ?? [])
          event.target.value = ''
          void upload(chosen)
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        disabled={isUploading}
        title={`${UPLOAD_ACCEPTED_LABEL}. ${UPLOAD_MANUAL_ENTRY_NOTE}`}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Upload className="mr-1 size-3" />}
        {isUploading ? 'Uploading…' : 'Add files'}
      </Button>
    </>
  )
}

export function AttachFilesButton({
  orderEventId,
  traderId,
  label
}: {
  orderEventId: number
  traderId: number
  label: string
}) {
  const invalidateOrderEvents = useOrderEventInvalidate()
  const { uploadOrderEventDocuments } = useUploadOrderEventDocuments()

  const attach = async (files: File[]) => {
    await uploadOrderEventDocuments({ files, orderEventId, traderId })
    await invalidateOrderEvents()
    toast({
      title: files.length === 1 ? 'File added' : `${files.length} files added`,
      description: `Added to the ${label.toLowerCase()}.`
    })
  }

  return <FilePickerButton label={label} multiple onFiles={attach} />
}

export function SendFilesButton({ traderId, label, hints }: { traderId: number; label: string; hints: UploadHints }) {
  const { user } = useAuth()
  const { send: sendForReview } = useSendForReview()

  if (user == null || !canSendSourceDocuments(user, traderId, hints)) return null

  const send = async (files: File[]) => {
    const failed = await sendForReview(files, traderId, hints)
    if (failed.length > 0) {
      throw new Error(failedUploadsMessage(failed, files.length))
    }
    toast({
      title: 'Sent to Klear for review',
      description: `Klear reads the files and files the ${label.toLowerCase()}. It shows here once Klear does.`
    })
  }

  return <FilePickerButton label={label} multiple={hints.childOrderEventId == null} onFiles={send} />
}
